import { useEventState } from '../lib/eventState';
import { plausibleEventClass } from '../lib/plausible';

/**
 * The designed footer (7.15 dev frames): a full-width black bar holding the
 * privacy statement + the copyright/nav row. This is the universal privacy
 * surface — always visible, not dismissible (it replaced the floating
 * PrivacyNoticeBar; the design's "Read more / Do Not Sell" labels don't map
 * to our pages, so the statement points to Privacy Settings instead,
 * client-side decision 2026-07-15). Links open in new tabs so visitors
 * don't lose the landing page (HX-17).
 *
 * The Official Rules link appears only during LIVE and POST (the giveaway is
 * not open during the pre-show countdown); the /rules route itself stays
 * reachable by direct URL in every phase. The `?phase=` QA override makes this
 * previewable without an admin flip.
 */

const BASE_LINKS = [
  { to: '/terms', label: 'Terms' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/cookies', label: 'Cookies' },
];

const RULES_LINK = { to: '/rules', label: 'Rules' };

/**
 * Plausible `UI Click` target for a footer destination. These open in NEW TABS,
 * so the resulting page view is a fresh entry with no referrer path back to
 * here — without tagging the click, nothing in the dashboard connects a
 * /rules read to the footer that sent them. Reading rate on the Rules link is
 * the one the legal side asks about.
 */
const footerTarget = (to: string): string => `footer-${to.replace(/^\//, '')}`;

function NewTabLink({ to, children }: { to: string; children: string }) {
  return (
    <a
      href={to}
      target="_blank"
      rel="noreferrer"
      className={`text-brand-red underline transition-colors hover:text-brand-white ${plausibleEventClass(
        'UI Click',
        { target: footerTarget(to) },
      )}`}
    >
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export function SiteFooter() {
  const [event] = useEventState();
  const showRules = event?.phase === 'LIVE' || event?.phase === 'POST';
  const links = showRules ? [...BASE_LINKS, RULES_LINK] : BASE_LINKS;

  return (
    // pt = the dev frame's 70px gap (texture shows through) between the page
    // content and the black bar.
    <footer className="w-full pt-[clamp(2.5rem,3.65vw,4.375rem)]">
      <div className="w-full bg-black">
        <div className="mx-auto flex w-full max-w-content flex-col items-center gap-[clamp(1rem,1.5vw,1.5625rem)] px-5 py-[clamp(1.25rem,1.5vw,1.5625rem)] text-center font-body text-[clamp(0.8rem,0.85vw,1rem)] leading-normal text-brand-white">
          <p>
            We value your privacy. We may collect personal information from you for business,
            marketing, and commercial purposes. Manage your choices anytime in{' '}
            <NewTabLink to="/privacy-settings">Privacy Settings</NewTabLink>.
          </p>
          <p className="uppercase">
            <span className="whitespace-nowrap">© 2026 HyperX</span>
            {links.map(({ to, label }) => (
              <span key={to} className="whitespace-nowrap">
                <span className="mx-3 select-none" aria-hidden="true">
                  |
                </span>
                <a
                  href={to}
                  target="_blank"
                  rel="noreferrer"
                  className={`transition-colors hover:text-brand-red ${plausibleEventClass(
                    'UI Click',
                    { target: footerTarget(to) },
                  )}`}
                >
                  {label}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </span>
            ))}
          </p>
        </div>
      </div>
    </footer>
  );
}
